import React, {useState, memo} from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {MenuItem, Typography} from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import PanoramaIcon from '@material-ui/icons/Panorama';
import PermMediaIcon from '@material-ui/icons/PermMedia';
import {
  addAssetFolder,
  addCameraFiles,
  addOrthoFiles,
  addPlanarFiles,
  addPointCloudFiles,
  openDialogFile,
  openDialogFolder,
  openDialogMulti,
  addTagCSV,
  addModelFiles
} from '../utilities';
import {
  modelFileFilter,
  imageCSVFilter,
  imageOrthoFilter,
  imagePlanarFilter,
  pointCloudFilter,
  tagCSVFilter
} from '../file-extensions';
import {AssetFolder} from "./folder";
import {addFolder, selectDefaultFolder, selectAllFolders} from '../redux/folders-slice';
import {selectAllAssets} from '../redux/assets-slice';
import {useDispatch, useSelector} from 'react-redux';
import {
  TextDialog,
  DenseIcon,
  DenseDivider,
  SlimScrollbar,
  FontAwesomeIcon,
  getAnchorPosition,
  initialMenuState,
  DenseMenu
} from '../components';
import path from 'path';
import { isStaticSite } from '../electron-modules';
import {toast} from '../app';
import { useTranslation } from 'react-i18next';
import CloudIcon from "@material-ui/icons/Cloud";
import RoomIcon from "@material-ui/icons/Room";
import PanoramaOutlinedIcon from "@material-ui/icons/PanoramaOutlined";
import SatelliteIcon from "@material-ui/icons/Satellite";
import { Viewer } from '../viewer/js/main';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addItem: {
      paddingTop: "0px",
      paddingBottom: "0px"
    }
  }),
);

export const AddAssetList = (props) => {
  const {folderID, closeContextMenu} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const handleAddModels = () => {
    closeContextMenu();

    openDialogMulti(modelFileFilter).then(paths => {
      if (paths.length === 0) return;

      addModelFiles(dispatch, paths, folderID);
    });
  };

  const handleAddPoints = () => {
    closeContextMenu();

    openDialogMulti(pointCloudFilter).then(paths => {
      if (paths.length === 0) return;

      addPointCloudFiles(dispatch, paths, folderID);
    });
  };

  const handleAddOrtho = () => {
    closeContextMenu();

    openDialogMulti(imageOrthoFilter).then(paths => {
      if (paths.length === 0) return;

      addOrthoFiles(dispatch, paths, folderID);
    });
  };

  const handleAddPanoramics = () => {
    closeContextMenu();

    openDialogFile(imageCSVFilter).then(async assetPath => {
      if (!assetPath) {
        return;
      }

      addCameraFiles(dispatch, [assetPath], folderID);
    });
  };

  const handleAddTags = async () => {
    closeContextMenu();

    openDialogFile(tagCSVFilter).then(async assetPath => {
      if (!assetPath) {
        return;
      }

      const filename = path.basename(assetPath);

      try {
        const tags: any = await addTagCSV(dispatch, assetPath, folderID);
        toast.success(t("toast.added-new-tags", {
          count: tags.length
        }));
      } catch (result) {
        const {reason, data} = result;

        if (reason === 'missing_headers') {
          toast.error(t("toast.tag-missing-header", {
            filename,
            missingHeaders: data.join(', ')
          }));

          return;
        }

        if (reason === 'invalid_rows') {
          toast.error(t("toast.tag-invalid-row", {
            filename,
            lineNumber: data[0].row_number,
            lineText: data[0].data
          }));

          return;
        }

        if (reason === 'no_data') {
          toast.error(t("toast.tag-no-data", {
            filename
          }));

          return;
        }
      }
    });
  };

  const handleAddPlanars = () => {
    closeContextMenu();

    openDialogFile(imagePlanarFilter).then(async assetPath => {
      if (!assetPath) return;

      addPlanarFiles(dispatch, [assetPath], folderID);
    });
  };

  return (
    <React.Fragment>
      <MenuItem onClick={handleAddPoints}>
        <DenseIcon>
          <CloudIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-point-cloud")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddPanoramics}>
        <DenseIcon>
          <PanoramaIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-panoramic-csv")}</Typography>

      </MenuItem>

      <MenuItem onClick={handleAddTags}>
        <DenseIcon>
          <RoomIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-tag-csv")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddPlanars}>
        <DenseIcon>
          <PanoramaOutlinedIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-planar-images")}</Typography>

      </MenuItem>

      <MenuItem onClick={handleAddOrtho}>
        <DenseIcon>
          <SatelliteIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-orthomosaic")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddModels}>
        <DenseIcon>
          <FontAwesomeIcon listIcon icon={"fa-cube"}/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-model-file")}</Typography>
      </MenuItem>
    </React.Fragment>
  );
};

export const FolderList = memo((props: any) => {
  const viewer = props.viewer as Viewer;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const defaultFolder = useSelector(selectDefaultFolder);
  const folders = useSelector(selectAllFolders);

  const [draggingAsset, setDraggingAsset] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuState, setMenuState] = useState({...initialMenuState});

  const handleSubmit = (name: string) => {
    dispatch(addFolder(name));
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleAddFolder = () => {
    openDialogFolder().then(({directoryPath, assetPaths}) => {
      assetPaths.then((paths) => {
        if (paths.length === 0) return;

        const folderName = path.basename(directoryPath);
        const action = dispatch(addFolder(folderName));
        const folderID = action.payload.id;
        addAssetFolder(dispatch, paths, folderID);
      });
    });
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    setMenuState({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleClose = () => {
    setMenuState(initialMenuState);
  };

  const handleNewFolder = () => {
    handleClose();
    setDialogOpen(true);
  };

  const menuOpen = menuState.mouseY !== null;
  const anchorPosition = getAnchorPosition(menuState);

  return (
    <React.Fragment>
      {/* Add item button */}
      {(!isStaticSite) && (<React.Fragment>
        <ListItem
          button
          onClick={handleContextMenu}
          className={classes.addItem}
        >
          <DenseIcon>
            <AddIcon fontSize="small"/>
          </DenseIcon>
          <ListItemText secondary={t("folder-list.add-item")}/>
        </ListItem>

        <DenseDivider variant="middle" spacing={10}/>
      </React.Fragment>)}

      <SlimScrollbar>
        <List disablePadding>
          {folders.map(folder =>
            <AssetFolder
              key={folder.id}
              folder={folder}
              assets={assets.filter(x => x.folderID === folder.id)}
              viewer={viewer}
              draggingAsset={draggingAsset}
              setDraggingAsset={setDraggingAsset}
            />)}
        </List>
      </SlimScrollbar>

      <TextDialog
        open={dialogOpen}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        title={t("folder-list.folder-prompt-title")}
        prompt={t("folder-list.folder-prompt")}
        default=''
        label={t("folder-list.folder-prompt-label")}
        clear={true}
      />

      <DenseMenu
        keepMounted
        open={menuOpen}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        <MenuItem onClick={handleNewFolder}>
          <DenseIcon>
            <CreateNewFolderIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t("folder-list.create-new-folder")}</Typography>
        </MenuItem>

        <AddAssetList
          folderID={defaultFolder.id}
          closeContextMenu={handleClose}
        />

        <MenuItem onClick={handleAddFolder}>
          <DenseIcon>
            <PermMediaIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t("folder-list.add-folder-contents")}</Typography>
        </MenuItem>
      </DenseMenu>
    </React.Fragment>
  );
});
